import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }

  code {
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"};
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }
`;

const ComparisonTable = styled.table`
  width: 100%;
  margin: 1.5rem 0;
  border-collapse: collapse;
  color: ${({ theme }) => theme.text};

  th {
    background-color: ${({ theme }) => theme.iconColor};
    color: ${({ theme }) => theme.background};
    padding: 0.75rem;
    text-align: left;
    font-weight: 600;
  }

  td {
    padding: 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.iconColor};
  }

  tr:nth-child(even) {
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
  }

  .strong-text {
    font-weight: 600;
    color: #10b981;
  }

  .weak-text {
    font-weight: 600;
    color: #ef4444;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PasswordSecurityGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.PASSWORD_SECURITY);

  // const heroImageSrc = "/images/guides/password-security.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Password Best Practices for SMBs"
        seoTitle="Password Security Guide: Best Practices for Small Businesses | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Learn how to create strong passwords, protect them, use multi-factor authentication, and implement password policies for your team."
        ogTitle="Password Security Guide: Best Practices | CSFSafe"
        ogDescription="Practical password security guidance for small business employees. Creation, storage, MFA, and rotation."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Password security and authentication"
      publishedLabel="Quick Reference Guide — July 21, 2026"
      showCTA={true}
    >
      <Paragraph>
        Weak passwords are one of the most exploited entry points for attackers.
        This guide provides practical password security practices for small
        business employees and teams. Follow these steps to protect your
        organization's critical accounts and data.
      </Paragraph>

      <H2>Creating Strong Passwords</H2>

      <H3>What Makes a Password Strong?</H3>

      <CalloutBox $type="tip">
        <strong>Strong Password Checklist</strong>
        <ul>
          <li>
            <strong>At least 12 characters long</strong> (longer is better than
            complex)
          </li>
          <li>
            <strong>Mix of character types:</strong> Uppercase, lowercase,
            numbers, and symbols
          </li>
          <li>
            <strong>Unique:</strong> Never reuse the same password across
            multiple accounts
          </li>
          <li>
            <strong>Unpredictable:</strong> Not based on dictionary words,
            common names, or personal information (birthdate, pet names, etc.)
          </li>
          <li>
            <strong>Unrelated to your username or role</strong>
          </li>
        </ul>
      </CalloutBox>

      <H3>Examples: Strong vs. Weak</H3>

      <ComparisonTable>
        <thead>
          <tr>
            <th>Weak Password</th>
            <th>Strong Password</th>
            <th>Why?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="weak-text">password123</td>
            <td className="strong-text">M7@kxQn9$pL2</td>
            <td>Weak: common word + numbers. Strong: random mix, 12 chars.</td>
          </tr>
          <tr>
            <td className="weak-text">abc123</td>
            <td className="strong-text">9T#vRw4&bZ1$Ky</td>
            <td>Weak: sequential. Strong: no pattern, mix of all types.</td>
          </tr>
          <tr>
            <td className="weak-text">JohnSmith2024</td>
            <td className="strong-text">Coffee7!Green&Maps</td>
            <td>
              Weak: uses name + year. Strong: random phrase, harder to guess.
            </td>
          </tr>
          <tr>
            <td className="weak-text">Password1!</td>
            <td className="strong-text">3L@x2Pw&9Q$mN</td>
            <td>Weak: predictable word. Strong: no recognizable words.</td>
          </tr>
        </tbody>
      </ComparisonTable>

      <H3>Passphrase Method (Easier to Remember)</H3>

      <Paragraph>
        If random passwords are hard to remember, try a passphrase: a sequence
        of random words with numbers and symbols mixed in.
      </Paragraph>

      <CalloutBox $type="tip">
        <strong>Passphrase Example</strong>
        <ul>
          <li>
            Think of a random sentence: "My cat eats 7 blue socks daily"
          </li>
          <li>
            Take first letters and add numbers/symbols: <code>Mce7bs*D!</code>
          </li>
          <li>Result: 10 characters, memorable, strong</li>
        </ul>
      </CalloutBox>

      <H2>Protecting Your Passwords</H2>

      <H3>Use a Password Manager</H3>

      <CalloutBox $type="do">
        <strong>DO: Use a Password Manager</strong>
        <ul>
          <li>
            <strong>Store all passwords:</strong> A password manager (like Bitwarden,
            1Password, Dashlane, or LastPass) securely stores passwords so you
            only need to remember one master password
          </li>
          <li>
            <strong>Generate strong passwords:</strong> Most password managers
            have a "generate password" feature for truly random passwords
          </li>
          <li>
            <strong>Sync across devices:</strong> Access your passwords on phone,
            tablet, and computer safely
          </li>
          <li>
            <strong>Share securely:</strong> Some managers let you share
            passwords with team members without exposing the actual password
          </li>
          <li>
            <strong>Monitor for breaches:</strong> Many alert you if your
            password appears in a known data breach
          </li>
        </ul>
      </CalloutBox>

      <H3>Never Share Passwords</H3>

      <CalloutBox $type="dont">
        <strong>DON'T Do These Things</strong>
        <ul>
          <li>
            <strong>Write passwords on sticky notes</strong> or in unencrypted
            documents
          </li>
          <li>
            <strong>Email passwords</strong> or share via messaging apps
          </li>
          <li>
            <strong>Tell anyone your password</strong> in person or over the
            phone
          </li>
          <li>
            <strong>Use public WiFi to log in</strong> to sensitive accounts
          </li>
          <li>
            <strong>Allow your browser to save passwords</strong> if your
            computer is used by others
          </li>
          <li>
            <strong>Reuse passwords</strong> across work and personal accounts
          </li>
          <li>
            <strong>Use the same password for multiple people</strong> to access
            one account (shared logins make accountability impossible)
          </li>
        </ul>
      </CalloutBox>

      <H2>Multi-Factor Authentication (MFA): The Game Changer</H2>

      <H3>What is MFA?</H3>

      <Paragraph>
        Multi-Factor Authentication (MFA) adds a second check beyond your
        password. Even if someone steals your password, they can't access your
        account without the second factor. This is one of the highest-impact
        security improvements you can make.
      </Paragraph>

      <H3>Types of MFA (From Weakest to Strongest)</H3>

      <CalloutBox $type="tip">
        <strong>MFA Methods Ranked</strong>
        <ul>
          <li>
            <strong>SMS text code (acceptable):</strong> A code texted to your
            phone. Better than nothing, but phone numbers can be hijacked.
          </li>
          <li>
            <strong>Email code (acceptable):</strong> A code sent to your email.
            Similar to SMS, more secure if your email is well-protected.
          </li>
          <li>
            <strong>Authenticator app (recommended):</strong> Apps like Microsoft
            Authenticator, Google Authenticator, or Authy generate a new code
            every 30 seconds. More secure than SMS.
          </li>
          <li>
            <strong>Security keys (strongest):</strong> Physical USB devices that
            you tap to prove your identity. Best protection against phishing,
            but requires hardware.
          </li>
        </ul>
      </CalloutBox>

      <H3>Enable MFA on Critical Accounts First</H3>

      <Paragraph>
        Not every account needs the same level of protection, but prioritize
        these:
      </Paragraph>

      <ul>
        <li>Email account (compromise = access to everything else)</li>
        <li>VPN or remote access</li>
        <li>Financial systems and banking</li>
        <li>Cloud storage (OneDrive, Google Drive, etc.)</li>
        <li>Work collaboration tools (Microsoft Teams, Slack, etc.)</li>
        <li>Privileged accounts (admin access)</li>
      </ul>

      <H2>Changing Your Password: When and How</H2>

      <H3>Periodic Password Changes</H3>

      <CalloutBox $type="tip">
        <strong>Password Rotation Schedule</strong>
        <ul>
          <li>
            <strong>Critical accounts:</strong> Change every 60–90 days, or if you
            suspect compromise
          </li>
          <li>
            <strong>Regular accounts:</strong> Change every 6–12 months, or if you
            suspect compromise
          </li>
          <li>
            <strong>After a breach:</strong> If your organization experiences a
            data breach involving this account, change immediately
          </li>
          <li>
            <strong>If compromised:</strong> If you suspect someone has access,
            change immediately and review account activity
          </li>
        </ul>
      </CalloutBox>

      <H3>Checking if Your Password Has Been Breached</H3>

      <Paragraph>
        Use a free service like <strong>Have I Been Pwned</strong> (haveibeenpwned.com)
        to check if your email or password appears in any known data breach. If it
        does, change your password immediately, especially if you reuse it
        elsewhere.
      </Paragraph>

      <H2>Password Policy for Organizations</H2>

      <H3>Best Practices to Implement</H3>

      <CalloutBox $type="tip">
        <strong>Recommended Password Policy</strong>
        <ul>
          <li>
            <strong>Minimum length:</strong> 12 characters (longer is better)
          </li>
          <li>
            <strong>Complexity:</strong> Mix of uppercase, lowercase, numbers,
            and symbols
          </li>
          <li>
            <strong>No reuse:</strong> Don't allow users to cycle through the
            same passwords
          </li>
          <li>
            <strong>No shared accounts:</strong> Every user should have their own
            account for accountability
          </li>
          <li>
            <strong>Password manager recommended:</strong> Provide or recommend
            a password manager for all staff
          </li>
          <li>
            <strong>MFA required:</strong> Especially for administrative accounts
            and remote access
          </li>
          <li>
            <strong>Offboarding process:</strong> When people leave, disable and
            change all shared account passwords
          </li>
        </ul>
      </CalloutBox>

      <H3>Avoid These Common Mistakes</H3>

      <CalloutBox $type="dont">
        <strong>DON'T Implement These</strong>
        <ul>
          <li>
            <strong>Don't require password changes every 30 days:</strong> This
            causes people to use predictable patterns or weak passwords
          </li>
          <li>
            <strong>Don't require overly complex passwords:</strong> People will
            write them down or reuse them
          </li>
          <li>
            <strong>Don't allow shared accounts:</strong> You can't track who
            accessed what
          </li>
          <li>
            <strong>Don't forbid password managers:</strong> Employees need
            somewhere to store strong passwords
          </li>
          <li>
            <strong>Don't let people store passwords in browsers:</strong> Too
            easy to access or extract
          </li>
        </ul>
      </CalloutBox>

      <H2>Training Your Team</H2>

      <Paragraph>
        Password security depends on everyone following best practices. Share
        these tips with your team:
      </Paragraph>

      <ul>
        <li>
          <strong>Educate:</strong> Train employees on password creation and
          security in onboarding
        </li>
        <li>
          <strong>Simulate phishing:</strong> Send fake phishing emails to see
          who's at risk of being tricked into sharing passwords
        </li>
        <li>
          <strong>Encourage reporting:</strong> If someone suspects their
          password is compromised, they should report it immediately
        </li>
        <li>
          <strong>Reinforce regularly:</strong> Password security reminders in
          newsletters or security awareness campaigns help
        </li>
        <li>
          <strong>Set the example:</strong> Leaders who follow best practices
          make it the norm for everyone
        </li>
      </ul>

      <H2>Quick Action Checklist</H2>

      <CalloutBox $type="tip">
        <strong>Do This This Week</strong>
        <ul>
          <li>Review your current passwords—any that are short or reused?</li>
          <li>
            Check haveibeenpwned.com to see if your accounts have been in a
            breach
          </li>
          <li>Set up a password manager (even free ones like Bitwarden help)</li>
          <li>Enable MFA on your email and critical work accounts</li>
          <li>Create a strong new password for one account today</li>
          <li>
            If your organization doesn't have a password policy, recommend one to
            leadership
          </li>
        </ul>
      </CalloutBox>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.ARTICLE.DUPLICATE_PASSWORDS)}>
            Unwinding Duplicate Passwords
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.ACCESS_CONTROL)}>
            Access Control &amp; Password Basics
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.PASSWORD_ATTACK)}>
            Password Attack Threats
          </a>
        </li>
        <li>
          <a href={href(LINKS.THREAT_TYPE.CREDENTIAL_STUFFING)}>
            Credential Stuffing
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.EMAIL_ACCOUNT_PROTECTION)}>
            Email Account Protection
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/password-security"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

PasswordSecurityGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default PasswordSecurityGuide;
